/* Field park: the CSV upload converts with the upload round (field-wrapper.md). */
import {
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Stack,
} from '@chakra-ui/react';
import { datadogLogs } from '@datadog/browser-logs';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Field, Text, TextField } from '@main/complykit';
import { useCreateQuestionnaireWithQuestionsMutation } from '@main/graphql/features/Questionnaires.admin.generated';
import { toError } from '@main/shared/utils';
import {
  errorToast,
  FileUpload,
  singleFileSchema,
  successToast,
  useLazyFileUpload,
} from '@main/ui';
import { FC } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod/v4';

type CreateQuestionnaireModalProps = {
  orgId: string;
  email?: string;
  onModalClose: () => void;
  isModalOpen: boolean;
};

const createQuestionnaireSchema = z.object({
  organizationId: z.string(),
  email: z
    .email('Enter a valid e-mail.')
    .max(200, 'Use 200 characters or fewer.')
    .or(z.literal('')),
  questionnaireName: z.string().min(1, 'Enter a name.'),
  questionnaireDescription: z.string().optional(),
  file: singleFileSchema().optional(),
});

type CreateQuestionnaireType = z.infer<typeof createQuestionnaireSchema>;

export const CreateQuestionnaireModal: FC<CreateQuestionnaireModalProps> = ({
  orgId,
  email,
  onModalClose,
  isModalOpen,
}) => {
  const modelHeaderText = 'Create questionnaire';
  const [createQuestionnaire] = useCreateQuestionnaireWithQuestionsMutation();

  const fileUpload = useLazyFileUpload({
    onFileAdd: (file) => {
      setValue('file', file, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    onFileDelete: () => {
      setValue('file', undefined, {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
  });
  const saveRequest: SubmitHandler<CreateQuestionnaireType> = async (data) => {
    let fileId: string | null = null;

    if (data.file) {
      const uploadedFileMeta = await fileUpload.upload();
      if (uploadedFileMeta.error) {
        errorToast('Error in uploading file.');
        return;
      }
      fileId = uploadedFileMeta.file.id;
    }

    await createQuestionnaire({
      object: {
        organizationId: orgId,
        questionnaireName: data.questionnaireName,
        questionnaireDescription: data.questionnaireDescription,
        email: data.email,
        fileId,
      },
    })
      .unwrap()
      .then(() => {
        successToast('Questionnaire created! You will receive an email with the results.');
        fileUpload.clear();
        reset();
        onModalClose();
      })
      .catch((error) => {
        errorToast('Unable to create questionnaire. Try again later.');
        datadogLogs.logger.error('Unable to create questionnaire.', {}, toError(error));
      });
  };

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<CreateQuestionnaireType>({
    resolver: zodResolver(createQuestionnaireSchema),
    defaultValues: {
      organizationId: orgId,
      questionnaireName: '',
      questionnaireDescription: '',
      email: email ?? '',
      file: undefined,
    },
    shouldFocusError: false,
  });

  return (
    <Modal
      size="xl"
      isCentered
      autoFocus={false}
      isOpen={isModalOpen}
      onClose={onModalClose}
      motionPreset="slideInBottom"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalBody pt={4} pb={0}>
          <form onSubmit={handleSubmit(saveRequest)} noValidate>
            <Stack spacing={6} pb={4}>
              <Text size="lg" weight="bold">
                {modelHeaderText}
              </Text>

              <Controller
                control={control}
                name="questionnaireName"
                render={({ field, fieldState }) => (
                  <TextField
                    ref={field.ref}
                    label="Name"
                    name={field.name}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    placeholder="Enter the name of Questionnaire"
                    error={fieldState.error?.message}
                  />
                )}
              />

              <Controller
                control={control}
                name="questionnaireDescription"
                render={({ field, fieldState }) => (
                  <TextField
                    ref={field.ref}
                    label="Description"
                    name={field.name}
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    placeholder="Enter the description of Questionnaire"
                    error={fieldState.error?.message}
                  />
                )}
              />

              <Field label="CSV file with questions" error={errors.file?.message}>
                {(fieldControl) => (
                  <FileUpload {...fileUpload.props}>
                    <FileUpload.Files />
                    <FileUpload.Dropzone inputProps={fieldControl} />
                  </FileUpload>
                )}
              </Field>

              <Controller
                control={control}
                name="email"
                render={({ field, fieldState }) => (
                  <TextField
                    ref={field.ref}
                    type="email"
                    label="E-mail to receive results"
                    name={field.name}
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    placeholder="Type your email to receive the results of the import"
                    error={fieldState.error?.message}
                  />
                )}
              />

              <Flex gap={2} justify="end">
                <Button variant="soft" isDisabled={isSubmitting} onPress={() => onModalClose()}>
                  Cancel
                </Button>
                <Button isPending={isSubmitting} type="submit">
                  Submit
                </Button>
              </Flex>
            </Stack>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
